/**
 * Pension of Hotel: hoe de organisatie haar pension noemt (Instellingen › Pension › Benaming, api
 * `GET /api/pension/benaming`). Het is alleen een woord in wat de gebruiker ziet; code, routes, api-paden en
 * tabellen blijven "pension".
 *
 * Twee wegen naar het scherm:
 *  - `benoem()` in de vertaallaag (i18nProvider.translate): elke vertaalde tekst;
 *  - een MutationObserver op de pagina voor de teksten die niet via de vertaling lopen (vaste Nederlandse
 *    teksten in de pensionschermen, namen die de api meestuurt) en voor title/placeholder/aria-label.
 *    Invoervelden, editors (contenteditable) en alles onder `data-benaming="vast"` blijven ongemoeid: wat
 *    iemand intypt of opslaat, wordt nooit herschreven.
 *
 * Samenstellingen gaan vanzelf mee (Pensionoverzichten → Hoteloverzichten, dierenpension → dierenhotel), en de
 * hoofdletters blijven zoals ze waren (PENSION → HOTEL).
 */
export type Benaming = "PENSION" | "HOTEL";

let actief: Benaming = "PENSION";
let waarnemer: MutationObserver | null = null;

// Nederlands "pension", Engels "boarding" (de Engelse teksten zeggen boarding kennel / boarding prices). Niet in
// "Docapension": dat is de naam van het oude pakket, geen pension.
const WOORD = /(?<!doca)pension|boarding/gi;
const HEEFT_WOORD = /(?<!doca)pension|boarding/i;
const ATTRIBUTEN = ["title", "placeholder", "aria-label", "alt"] as const;
const OVERSLAAN = "input, textarea, select, script, style, [contenteditable], [contenteditable] *, [data-benaming='vast'], [data-benaming='vast'] *";

/** Vervangt "pension" door "hotel" in een tekst, met behoud van hoofdletters; ongewijzigd als de benaming Pension is. */
export function benoem(tekst: string): string {
  if (actief === "PENSION" || !tekst || !HEEFT_WOORD.test(tekst)) return tekst;
  return tekst.replace(WOORD, (w) =>
    w === w.toUpperCase() ? "HOTEL" : w[0] === w[0].toUpperCase() ? "Hotel" : "hotel");
}

export function huidigeBenaming(): Benaming {
  return actief;
}

function herschrijfTekstknoop(knoop: Text) {
  const ouder = knoop.parentElement;
  if (!ouder || ouder.closest(OVERSLAAN)) return;
  const nieuw = benoem(knoop.data);
  if (nieuw !== knoop.data) knoop.data = nieuw;
}

function herschrijfAttributen(el: Element) {
  if (el.closest(OVERSLAAN) && !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
  if (el.closest("[data-benaming='vast']")) return;
  for (const naam of ATTRIBUTEN) {
    const waarde = el.getAttribute(naam);
    if (waarde && HEEFT_WOORD.test(waarde)) el.setAttribute(naam, benoem(waarde));
  }
}

function herschrijf(wortel: Node) {
  if (wortel.nodeType === Node.TEXT_NODE) {
    herschrijfTekstknoop(wortel as Text);
    return;
  }
  if (!(wortel instanceof Element)) return;
  herschrijfAttributen(wortel);
  const loper = document.createTreeWalker(wortel, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = loper.nextNode(); n; n = loper.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) herschrijfTekstknoop(n as Text);
    else herschrijfAttributen(n as Element);
  }
}

/** Zet de benaming; bij Hotel wordt de pagina meteen en daarna bij elke wijziging bijgewerkt. */
export function zetBenaming(benaming: Benaming | null | undefined): void {
  const nieuw: Benaming = benaming === "HOTEL" ? "HOTEL" : "PENSION";
  if (nieuw === actief) return;
  actief = nieuw;
  waarnemer?.disconnect();
  waarnemer = null;
  if (typeof document === "undefined") return;
  if (actief === "HOTEL") {
    herschrijf(document.body);
    document.title = benoem(document.title);
    waarnemer = new MutationObserver((wijzigingen) => {
      for (const w of wijzigingen) {
        if (w.type === "characterData") herschrijfTekstknoop(w.target as Text);
        else if (w.type === "attributes") herschrijfAttributen(w.target as Element);
        else w.addedNodes.forEach(herschrijf);
      }
    });
    waarnemer.observe(document.body, {
      subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRIBUTEN],
    });
  }
  // Terug naar Pension: wat al herschreven was, staat er tot de volgende render; een herlaad is genoeg.
}
